// 2. DocumentUploadCard.js - Updated to handle pre-uploaded documents
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { FILE_UPLOAD_STATUS, FILE_ACCEPT_MAP } from "../../constants";
import FileUpload from "./fileUpload";
import "./styles.scss";
import { parseServerNameMeta } from "../../utils/document";

const normalize = (v = "") => String(v).trim().toLowerCase();

const normalizeGroupKeyForCompare = (groupKey = "") => {
  return normalize(groupKey);
};

const extractProviderIdFromParentKey = (parentFileKey = "") => {
  const g = normalize(parentFileKey);
  if (g.startsWith("bank-")) return g.replace("bank-", "");
  return "";
};

const DocumentUploadCard = ({
  documents,
  handleFileUpload,
  fileUploadStatus = {},
  onAllRequiredUploaded,
  fileKey: parentFileKey = "default",
  documentList = [],
  onDeleteDocument,
  serverGroupKey,
  isLoadingDocuments = false,
  groupKey = "",
  providerId = "",
}) => {
  const [requiredFiles, setRequiredFiles] = useState({});
  const [uploadedDocuments, setUploadedDocuments] = useState({});

  const documentTypeMapping = useMemo(() => {
    const mapping = {};
    documents?.forEach((doc, index) => {
      const key = `${parentFileKey}-${doc.type}-${index}`;
      const apiDocType = doc.type?.toUpperCase() || "DOCUMENT";
      mapping[key] = apiDocType;
    });
    return mapping;
  }, [documents, parentFileKey]);

  useEffect(() => {
    if (!documentList?.length) return;

    const uploaded = {};
    const expectedGroup = normalize(serverGroupKey || parentFileKey);
    const expectedProvider = normalize(
      providerId ||
        extractProviderIdFromParentKey(serverGroupKey || parentFileKey)
    );

    documents.forEach((doc, index) => {
      const fileKey = `${parentFileKey}-${doc.type}-${index}`;

      const uploadedDoc = documentList.find((srv) => {
        const sameType = normalize(srv.document_type) === normalize(doc.type);
        if (!sameType) return false;

        if (srv.status !== "COMPLETED") return false;

        const accepted = Array.isArray(doc?.accetFileType)
          ? doc.accetFileType
          : [];
        const srvFormat = normalize(srv?.format || "");
        const sameFormat = accepted.length
          ? accepted.map(normalize).includes(srvFormat)
          : true;
        if (!sameFormat) return false;

        const meta = parseServerNameMeta(srv.name);

        const sameGroup = normalize(meta.groupKey) === expectedGroup;

        const sameSlot = Number(meta.slotIndex) === Number(index);

        const metaProvider = normalize(meta.providerId || "");
        const metaProviderBase = metaProvider.split("-")[0];
        const expectedProviderBase = expectedProvider.split("-")[0];

        const sameProvider = expectedProvider
          ? metaProvider === expectedProvider ||
            metaProviderBase === expectedProviderBase
          : true;

        return sameGroup && sameSlot && sameProvider;
      });

      if (uploadedDoc) uploaded[fileKey] = uploadedDoc;
    });

    setUploadedDocuments(uploaded);
  }, [documentList, documents, parentFileKey, serverGroupKey, providerId]);

  useEffect(() => {
    const initialFiles = {};
    let hasRequired = false;
    documents?.forEach((doc, index) => {
      const isRequired = doc.isRequired;
      if (isRequired) {
        hasRequired = true;
        const key = `${parentFileKey}-${doc.type}-${index}`;
        const isAlreadyUploaded = !!uploadedDocuments[key];
        initialFiles[key] = {
          required: true,
          uploaded:
            isAlreadyUploaded ||
            fileUploadStatus[key] === FILE_UPLOAD_STATUS.COMPLETED,
          alreadyUploaded: isAlreadyUploaded,
        };
      }
    });

    setRequiredFiles(initialFiles);

    // Notify parent about initial status
    if (onAllRequiredUploaded && hasRequired) {
      const allUploaded = Object.values(initialFiles).every(
        (file) => file.uploaded
      );
      onAllRequiredUploaded(allUploaded);
    }
  }, [
    documents,
    parentFileKey,
    onAllRequiredUploaded,
    uploadedDocuments,
    fileUploadStatus,
  ]);

  // Update tracking when fileUploadStatus changes
  useEffect(() => {
    const updatedFiles = { ...requiredFiles };
    let hasChanges = false;

    Object.keys(requiredFiles).forEach((key) => {
      const isUploaded =
        fileUploadStatus[key] === FILE_UPLOAD_STATUS.COMPLETED ||
        !!uploadedDocuments[key];
      if (updatedFiles[key].uploaded !== isUploaded) {
        updatedFiles[key].uploaded = isUploaded;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setRequiredFiles(updatedFiles);

      // Check if all required files are uploaded
      const allUploaded = Object.values(updatedFiles).every(
        (file) => file.uploaded
      );
      if (onAllRequiredUploaded) {
        onAllRequiredUploaded(allUploaded);
      }
    }
  }, [
    fileUploadStatus,
    requiredFiles,
    uploadedDocuments,
    onAllRequiredUploaded,
  ]);

  const handleFileUploadWithTracking = useCallback(
    (fileData, key, meta) => {
      if (requiredFiles[key]) {
        setRequiredFiles((prev) => ({
          ...prev,
          [key]: { ...prev[key], uploaded: true, alreadyUploaded: false },
        }));

        if (onAllRequiredUploaded) {
          const allUploaded = Object.values({
            ...requiredFiles,
            [key]: { uploaded: true },
          }).every((file) => file.uploaded);
          onAllRequiredUploaded(allUploaded);
        }
      }

      return handleFileUpload(fileData, key, meta);
    },
    [requiredFiles, handleFileUpload, onAllRequiredUploaded]
  );

  const handleFileDeleteWithTracking = useCallback(
    async (key, documentId) => {
      // If there's a server document ID, delete from server first
      if (documentId && onDeleteDocument) {
        const success = await onDeleteDocument(documentId);
        if (!success) return; // Don't update local state if delete failed
      }

      // Update tracking when file is deleted
      if (requiredFiles[key]) {
        setRequiredFiles((prev) => ({
          ...prev,
          [key]: { ...prev[key], uploaded: false, alreadyUploaded: false },
        }));

        // Remove from uploaded documents
        setUploadedDocuments((prev) => {
          const newUploaded = { ...prev };
          delete newUploaded[key];
          return newUploaded;
        });

        // Notify parent
        if (onAllRequiredUploaded) {
          const allUploaded = Object.values({
            ...requiredFiles,
            [key]: { uploaded: false },
          }).every((file) => file.uploaded);
          onAllRequiredUploaded(allUploaded);
        }
      }
    },
    [requiredFiles, onAllRequiredUploaded, onDeleteDocument]
  );
  return (
    <div className="document-upload-card">
      {documents?.map((doc, index) => {
        const fileKey = `${parentFileKey}-${doc.type}-${index}`;
        const meta = {
          group_key: serverGroupKey || parentFileKey,
          slot_index: index,
          provider_id: providerId
            ? String(providerId).toLowerCase()
            : undefined,
        };
        const isUploading =
          fileUploadStatus[fileKey] === FILE_UPLOAD_STATUS.IN_PROGRESS;
        const isCompleted =
          fileUploadStatus[fileKey] === FILE_UPLOAD_STATUS.COMPLETED;
        const isRequired = doc.isRequired !== false;
        const uploadedDoc = uploadedDocuments[fileKey];
        const isAlreadyUploaded = !!uploadedDoc;
        const serverDocumentId = uploadedDoc?.id;

        let dynamicAccept = "application/pdf";

        if (Array.isArray(doc?.accetFileType) && doc.accetFileType.length > 0) {
          dynamicAccept = doc.accetFileType
            .map((ext) => FILE_ACCEPT_MAP[ext.toUpperCase()] || "")
            .filter((x) => x)
            .join(",");
        }
        return (
          <FileUpload
            key={fileKey}
            fileKey={fileKey}
            fileName={doc?.label}
            isRequired={isRequired}
            accept={dynamicAccept}
            maxSize={
              doc?.maxSize
                ? Number(doc?.maxSize) * 1024 * 1024
                : 10 * 1024 * 1024
            }
            docType={doc?.type}
            onFileDelete={(key) =>
              handleFileDeleteWithTracking(key, serverDocumentId)
            }
            onClickTryAgain={(fileData, key) =>
              handleFileUploadWithTracking(fileData, key, meta)
            }
            onFileChange={(fileData, key) =>
              handleFileUploadWithTracking(fileData, key, meta)
            }
            index={index}
            fileType={doc?.fileType}
            rightIcon={doc.rightIcon}
            toolTip={doc.toolTip}
            isUploading={isUploading}
            isCompleted={isCompleted || isAlreadyUploaded}
            fileUploadStatus={fileUploadStatus}
            isAlreadyUploaded={isAlreadyUploaded}
            uploadedFileName={uploadedDoc?.name}
            uploadedFileSize={uploadedDoc?.size}
            uploadedDocFormat={uploadedDoc?.format}
            documentSize={doc?.maxSize}
          />
        );
      })}
    </div>
  );
};

export default DocumentUploadCard;
